// ═══════════════════════════════════════════════════════════
// New Request — staff manual intake form
// (phone/mail/email/in-person requests logged by staff)
// ═══════════════════════════════════════════════════════════

import { useState, useEffect, FormEvent } from 'react';
import { maskPhone } from '../../../lib/format';
import { useNavigate, Link } from 'react-router-dom';
import { IconArrowLeft, IconUser, IconFileDescription, IconCalendar, IconLoader2 } from '@tabler/icons-react';
import { createRequest, type RequestChannel, type RequesterType } from '../../../api/requests';
import { listDepartments, type Department } from '../../../api/departments';
import { useRequestStore } from '../../../store/useRequestStore';

const CHANNELS: { value: RequestChannel; label: string }[] = [
  { value: 'phone',    label: 'Phone' },
  { value: 'email',    label: 'Email' },
  { value: 'mail',     label: 'Mail / Letter' },
  { value: 'inPerson', label: 'In Person' },
  { value: 'portal',   label: 'Portal (staff-entered)' },
];

const REQUESTER_TYPES: { value: RequesterType; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'business',   label: 'Business / Organization' },
  { value: 'media',      label: 'Media / Journalist' },
  { value: 'attorney',   label: 'Attorney / Legal' },
  { value: 'government', label: 'Government Entity' },
  { value: 'other',      label: 'Other' },
];

const PRIORITIES = [
  { value: 'normal', label: 'Normal' },
  { value: 'high',   label: 'High' },
  { value: 'urgent', label: 'Urgent' },
];

interface FormData {
  requesterName:  string;
  requesterEmail: string;
  requesterPhone: string;
  requesterType:  RequesterType;
  requesterOrg:   string;
  subject:        string;
  description:    string;
  dateReceived:   string;
  channel:        RequestChannel;
  departmentId:   string;
  priority:       string;
  staffNotes:     string;
}

const showOrg = (t: RequesterType) =>
  ['business', 'media', 'attorney', 'government'].includes(t);

export default function NewRequest() {
  const navigate    = useNavigate();
  const refresh     = useRequestStore(s => s.refresh);

  const [saving, setSaving]         = useState(false);
  const [departments, setDepts]     = useState<Department[]>([]);
  const [error, setError]           = useState('');

  const [form, setForm] = useState<FormData>({
    requesterName:  '',
    requesterEmail: '',
    requesterPhone: '',
    requesterType:  'individual',
    requesterOrg:   '',
    subject:        '',
    description:    '',
    dateReceived:   new Date().toISOString().split('T')[0],
    channel:        'phone',
    departmentId:   '',
    priority:       'normal',
    staffNotes:     '',
  });

  useEffect(() => {
    listDepartments().then(setDepts).catch(() => {});
  }, []);

  function update<K extends keyof FormData>(key: K, value: FormData[K]) {
    setForm(f => ({ ...f, [key]: value }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setSaving(true);

    try {
      // Split name into first/last
      const nameParts  = form.requesterName.trim().split(/\s+/);
      const firstName  = nameParts[0] ?? '';
      const lastName   = nameParts.slice(1).join(' ');

      const result = await createRequest({
        requesterFirstName: firstName,
        requesterLastName:  lastName || undefined,
        requesterEmail:     form.requesterEmail || undefined,
        requesterPhone:     form.requesterPhone || undefined,
        requesterOrg:       form.requesterOrg   || undefined,
        requesterType:      form.requesterType,
        subject:            form.subject.trim(),
        description:        form.description.trim(),
        dateReceived:       form.dateReceived,
        channel:            form.channel,
        priority:           form.priority as 'normal' | 'high' | 'urgent',
        departmentId:       form.departmentId  || undefined,
        jurisdictionId:     import.meta.env.VITE_JURISDICTION_ID ?? '1',
        internalNotes:      form.staffNotes    || undefined,
        isAnonymous:        false,
      });

      // Refresh the store so the new request appears in the list
      await refresh();

      const newId = (result.request as Record<string, unknown>)['record_id'];
      navigate(`/staff/requests/${newId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create request. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div style={{ padding: 0, maxWidth: 780, margin: '0 auto' }}>
      {/* Back nav */}
      <div style={{ marginBottom: 24 }}>
        <Link
          to="/staff/requests"
          style={{ color: 'var(--color-primary-700)', textDecoration: 'none', fontSize: 14, display: 'inline-flex', alignItems: 'center', gap: 4 }}
        >
          <IconArrowLeft size={13} /> Back to Requests
        </Link>
      </div>

      <h1 style={{ fontSize: 20, fontWeight: 700, color: 'var(--color-neutral-900)', margin: '0 0 4px' }}>
        Log New Request
      </h1>
      <p style={{ fontSize: 14, color: 'var(--color-neutral-500)', margin: '0 0 24px' }}>
        Manually record a request received by phone, mail, email, or in-person visit.
      </p>

      {error && (
        <div style={{
          marginBottom: 16, padding: '10px 14px', background: '#FBEFED',
          border: '1px solid #F7D5D0', borderRadius: 4,
          fontSize: 14, color: 'var(--cds-red-700)',
        }}>
          {error}
        </div>
      )}

      <form onSubmit={onSubmit}>
        {/* ── Section: Requester ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <IconUser size={14} style={{ color: 'var(--color-neutral-400)' }} />
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
              Requester Information
            </span>
          </div>
          <div style={{ padding: '16px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 20px' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Full Name</label>
              <input
                type="text" className="form-input"
                value={form.requesterName}
                onChange={e => update('requesterName', e.target.value)}
                required placeholder="Jane Smith"
              />
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Requester Type</label>
              <select className="form-input" value={form.requesterType} onChange={e => update('requesterType', e.target.value as RequesterType)}>
                {REQUESTER_TYPES.map(rt => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
              </select>
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Email Address</label>
              <input
                type="email" className="form-input"
                value={form.requesterEmail}
                onChange={e => update('requesterEmail', e.target.value)}
                placeholder="requester@email.com"
              />
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Phone Number</label>
              <input
                type="tel" className="form-input"
                value={form.requesterPhone}
                onInput={maskPhone}
                onChange={e => update('requesterPhone', e.target.value)}
                placeholder="(555) 123-4567"
              />
            </div>
            {showOrg(form.requesterType) && (
              <div className="form-field" style={{ marginBottom: 0, gridColumn: '1 / -1' }}>
                <label className="form-label">Organization / Employer</label>
                <input
                  type="text" className="form-input"
                  value={form.requesterOrg}
                  onChange={e => update('requesterOrg', e.target.value)}
                  placeholder="Organization name"
                />
              </div>
            )}
          </div>
        </div>

        {/* ── Section: Request Details ── */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)', display: 'flex', alignItems: 'center', gap: 7 }}>
            <IconFileDescription size={14} style={{ color: 'var(--color-neutral-400)' }} />
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
              Request Details
            </span>
          </div>
          <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Subject / Title</label>
              <input
                type="text" className="form-input"
                value={form.subject}
                onChange={e => update('subject', e.target.value)}
                required placeholder="Brief description of the records requested"
              />
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label required">Request Description</label>
              <textarea
                className="form-input" rows={5}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
                value={form.description}
                onChange={e => update('description', e.target.value)}
                required
                placeholder="Full text of the requester's request, as received…"
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0 20px' }}>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label required">Date Received</label>
                <div style={{ position: 'relative' }}>
                  <IconCalendar size={14} style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--color-neutral-400)' }} />
                  <input
                    type="date" className="form-input"
                    style={{ paddingLeft: 30 }}
                    value={form.dateReceived}
                    onChange={e => update('dateReceived', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label required">Channel</label>
                <select className="form-input" value={form.channel} onChange={e => update('channel', e.target.value as RequestChannel)} required>
                  {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-field" style={{ marginBottom: 0 }}>
                <label className="form-label">Priority</label>
                <select className="form-input" value={form.priority} onChange={e => update('priority', e.target.value)}>
                  {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>
            </div>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <label className="form-label">Assign to Department</label>
              <select className="form-input" value={form.departmentId} onChange={e => update('departmentId', e.target.value)}>
                <option value="">— Assign later —</option>
                {departments.map(d => (
                  <option key={d.recordId} value={String(d.recordId)}>{d.name}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* ── Section: Staff Notes ── */}
        <div className="card" style={{ marginBottom: 24 }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-neutral-100)' }}>
            <span style={{ fontSize: 12, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-neutral-600)' }}>
              Internal Notes
            </span>
            <span style={{ fontSize: 12, color: 'var(--color-neutral-400)', marginLeft: 8 }}>Not visible to requester</span>
          </div>
          <div style={{ padding: '16px' }}>
            <div className="form-field" style={{ marginBottom: 0 }}>
              <textarea
                className="form-input" rows={3}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
                value={form.staffNotes}
                onChange={e => update('staffNotes', e.target.value)}
                placeholder="Any initial observations, related requests, complexity flags…"
              />
            </div>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Link to="/staff/requests" className="btn btn-secondary">
            Cancel
          </Link>
          <button type="submit" className="btn btn-primary" disabled={saving} style={{ gap: 8 }}>
            {saving
              ? <><IconLoader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> Creating…</>
              : 'Create Request'
            }
          </button>
        </div>
      </form>
    </div>
  );
}
